/**
 * `BarsV` — spec §F2 `BarsV(labels, values, {colors, h, unit})`: "value labels above bars".
 *
 * The mockup's `barsV()`: a 290×120 box, 18px of label gutter at the bottom, bars ≤30px
 * wide on an even pitch, the value printed above each bar and the category beneath it, and
 * a 15 % headroom over the tallest bar so that top label is never clipped. No axis is drawn
 * — §F2 gives this chart four categories and a printed number per bar, which is one axis'
 * worth of information already (§G8: "one axis per chart").
 *
 * What real data adds to the mockup's version:
 *
 * 1. **Missing values draw no bar.** §D3 omits units with no data (never zero), so the
 *    caller passes `null` and gets an em dash over an empty slot — a zero-height bar would
 *    claim the country reported nothing *because it achieved nothing*.
 * 2. **A zero/negative maximum is survivable.** `v / 0` is the difference between an empty
 *    chart and a chart of `NaN` path data.
 * 3. **Colours are per bar, positionally supplied by the caller** — which is how §G8's
 *    fixed country palette reaches it (`lib/palette.ts`), rather than this chart knowing
 *    what a country is.
 * 4. **Hover through the shared `ChartTip` portal** (§F2), not the mockup's `data-tip` pass.
 *
 * Both printed numbers are ink tokens, never the bar colour (§F2/§G8).
 */
import { useMemo } from 'react';

import { EMDASH, fmtNum } from '../../lib/format';
import { hideChartTip, showChartTip } from './chartTipStore';

/** Mockup geometry — the label gutter, the top padding and the widest a bar may get. */
export const BARSV_PAD_BOTTOM = 18;
export const BARSV_PAD_TOP = 8;
export const BARSV_MAX_BAR_WIDTH = 30;
/** The mockup's `max * 1.15`: headroom so the tallest bar's value label stays inside. */
export const BARSV_HEADROOM = 1.15;

export interface BarsVProps {
  labels: readonly string[];
  /** One per label; `null`/`undefined`/non-finite = not reported (no bar, em-dash label). */
  values: readonly (number | null | undefined)[];
  /** One per bar, positionally (§G8 fixed categorical order). Defaults to the brand green. */
  colors?: readonly string[];
  w?: number;
  h?: number;
  /** Appended to the tooltip value (`1,204 ha`). */
  unit?: string;
  /** §G3 decimals, for both the printed label and the tooltip. */
  fmt?: number;
  ariaLabel?: string;
  className?: string;
}

const DEFAULT_COLOR = '#2E7D32';

export function BarsV({
  labels,
  values,
  colors,
  w = 290,
  h = 120,
  unit = '',
  fmt = 0,
  ariaLabel,
  className,
}: BarsVProps) {
  const bars = useMemo(() => {
    const n = Math.max(labels.length, 1);
    const numbers = labels.map((_, i) => {
      const v = values[i];
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    });
    const peak = Math.max(0, ...numbers.map((v) => v ?? 0));
    const max = peak > 0 ? peak * BARSV_HEADROOM : 1;
    const pitch = (w - 10) / n;
    const barWidth = Math.max(4, Math.min(BARSV_MAX_BAR_WIDTH, pitch - 8));
    const plot = h - BARSV_PAD_TOP - BARSV_PAD_BOTTOM;

    return labels.map((label, i) => {
      const v = numbers[i] ?? null;
      const height = v === null ? 0 : Math.max(0, (v / max) * plot);
      const x = 6 + i * pitch;
      return { label, v, x, barWidth, height, y: h - BARSV_PAD_BOTTOM - height, i };
    });
  }, [labels, values, w, h]);

  const valueText = (v: number | null) =>
    v === null ? EMDASH : `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  return (
    <svg
      className={`chart barsv${className ? ` ${className}` : ''}`}
      width="100%"
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label={ariaLabel ?? `${labels.length} categories`}
      data-bars={bars.filter((b) => b.v !== null).length}
      onMouseLeave={hideChartTip}
    >
      {bars.map((b) => (
        <g key={b.label} data-barsv-bar={b.i} data-label={b.label}>
          {b.v === null ? null : (
            <rect
              x={b.x}
              y={b.y}
              width={b.barWidth}
              height={b.height}
              rx={3.5}
              fill={colors?.[b.i] ?? DEFAULT_COLOR}
              style={{ cursor: 'pointer' }}
              data-value={valueText(b.v)}
              onMouseEnter={(e) => showChartTip(e, b.label, valueText(b.v))}
              onMouseMove={(e) => showChartTip(e, b.label, valueText(b.v))}
              onMouseLeave={hideChartTip}
            />
          )}
          <text
            x={b.x + b.barWidth / 2}
            y={b.y - 4}
            textAnchor="middle"
            fontSize={9.5}
            fontWeight={700}
            fill="#22301F"
            data-barsv-value={b.i}
          >
            {b.v === null ? EMDASH : fmtNum(b.v, fmt)}
          </text>
          <text
            x={b.x + b.barWidth / 2}
            y={h - 5}
            textAnchor="middle"
            fontSize={9}
            fill="#8B958A"
            data-barsv-label={b.i}
          >
            {b.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

export default BarsV;
